import { motion } from "framer-motion";
import { Apple, Carrot, Flower2, Leaf, Sprout, Wheat } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";

const iconMap = {
  wheat: Wheat,
  sprout: Sprout,
  leaf: Leaf,
  carrot: Carrot,
  apple: Apple,
  flower: Flower2,
};

const ease = [0.22, 1, 0.36, 1];

export default function CropGuide() {
  const content = useContent();
  const { t } = useLang();
  const crops = content.crops;
  return (
    <section
      id="crops"
      data-testid="crops-section"
      className="border-t border-line"
    >
      <div className="mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-40">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
              className="text-xs uppercase tracking-[0.3em] text-harvest"
            >
              {t("cropsOverline")}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease }}
              data-testid="crops-heading"
              className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl"
            >
              {t("cropsHeadA")} <br />
              <span className="italic text-harvest">{t("cropsHeadAccent")}</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease }}
            className="max-w-md text-sm leading-relaxed text-sage sm:text-base lg:col-span-4"
          >
            {t("cropsIntro")}
          </motion.p>
        </div>

        <div className="mt-16 grid grid-cols-1 border border-line sm:grid-cols-2 lg:grid-cols-3">
          {crops.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease }}
              data-testid={`crop-card-${i}`}
              className="group border-line p-10 transition-colors duration-500 hover:bg-moss [&:not(:last-child)]:border-b sm:[&:nth-child(odd)]:border-r lg:[&:nth-child(3n)]:border-r-0 lg:[&:nth-child(-n+3)]:border-b sm:[&:nth-child(-n+4)]:border-b lg:[&:nth-child(-n+4)]:border-b-0 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r"
            >
              <span className="inline-flex border border-line p-3 text-harvest transition-colors duration-300 group-hover:border-harvest">
                {(() => {
                  const Icon = iconMap[c.icon] || Sprout;
                  return <Icon className="h-6 w-6" />;
                })()}
              </span>
              <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-cream sm:text-2xl">
                {c.name}
              </h3>
              <p className="mt-3 text-sm font-semibold text-harvest">
                {t("recommended")} {c.system}
              </p>
              <p className="mt-2 text-xs uppercase tracking-[0.15em] text-sage">
                {t("savingUpTo")} {c.saving}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
